import React from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { AxisOptions, Chart } from "react-charts";
import { Spinner } from "react-bootstrap";

import { useAuthContext } from "./hooks/useAuthContext";
import { useLogout } from "./hooks/useLogout";

import ResizableBox from "./ResizableBox";
import StreckTable from "./StreckTable";
import CommentsTable from "./CommentsTable";
import { checkResponse } from "./utils";

type Streck = { date: string, streck: number }
type StreckStats = { _id: string, streckStats: { date: string, stats: Record<string, number>[] } }
type DumstreckStats = { _id: string, person: string, dumstreck: number }
type StadstreckStats = { _id: string, person: string, stadstreck: number }
type StatsType = { stadstreck: StadstreckStats, dumstreck: DumstreckStats }

export type Comment = { 
    _id?: string, 
    comment: string, 
    position: string, 
    date: string, 
    dateStr?: string, 
    streck: string,
    count: number,
    updatedAt?: string,
    createdAt?: string,
    __v?: number,
 }

function formatDateToMonthDay(inputDate: string) {
    const months = [
        "januari", "februari", "mars", "april", "maj", "juni",
        "juli", "augusti", "september", "oktober", "november", "december"
    ];

    // Create a Date object from the input date string
    const dateObj = new Date(inputDate);

    // Extract day and month
    const day = dateObj.getDate();
    const month = months[dateObj.getMonth()];

    // Format the output string
    const formattedDate = `${day} ${month}`;

    return formattedDate;
}

function transformData(inputData: StreckStats[]) {
    const resultMap = new Map();

    for (const item of inputData) {

        const { streckStats } = item;
        const { date, stats } = streckStats;

        for (const [label, streck] of Object.entries(stats)) {

            if (label === "_id") continue;
            const dataItem = { date: formatDateToMonthDay(date), streck };
            if (!resultMap.has(label)) {
                resultMap.set(label, { label, data: [dataItem] });
            } else {
                resultMap.get(label).data.push(dataItem);
            }
        }
    }

    return Array.from(resultMap.values());
}

const colors = [
    "#1f77b4", // Dark blue
    "#ff7f0e", // Orange
    "#2ca02c", // Green
    "#d62728", // Red
    "#9467bd", // Purple
    "#8c564b", // Brown
    "#e377c2", // Pink
    "#ff9896", // Brighter pink
    "#bcbd22", // Olive
    "#17becf", // Light blue
    "#ff33cc", // Magenta
];

//TODO: show datum circles when only one entry in chart
export default function Stats(props: { theme: string; }) {
    const [{ activeSeriesIndex, activeDatumIndex }, setState] = React.useState({
        activeSeriesIndex: -1,
        activeDatumIndex: -1,
    });

    const [dumstreckData, setDumstreckData] = useState<any[]>([]);
    const [stadstreckData, setStadstreckData] = useState([]);
    const [topThreeDumstreck, setTopThreeDumstreck] = useState([]);
    const [topThreeStadstreck, setTopThreeStadstreck] = useState([]);
    const [comments, setComments] = useState<Comment[]>([]);

    const { user } = useAuthContext();
    const { logout } = useLogout();

    const dark = props.theme === 'dark' ? true : false;

    const getStats = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stats/getStats`, {
            headers: { Authorization: `Bearer ${user.token}` },
        });

        if (!await checkResponse(response, logout)) return false;

        const stats = await response.json();

        const dumstreckData = stats
            .filter((item: StatsType) => "dumstreck" in item)
            .map(({ dumstreck }: StatsType) => ({ streckStats: dumstreck }));

        // Separate objects with key "stadstreck" and rename the key to "streckStats"
        const stadstreckData = stats
            .filter((item: StatsType) => "stadstreck" in item)
            .map(({ stadstreck }: StatsType) => ({ streckStats: stadstreck }));


        setDumstreckData(dumstreckData);
        setStadstreckData(stadstreckData);
    }, [user.token, logout])


    const getTopThreeStats = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stats/total/getTopThree`, {
            headers: { Authorization: `Bearer ${user.token}` },
        });

        if (!await checkResponse(response, logout)) return false;

        const streck = await response.json();

        let topThreeDumstreck = streck.dumstreck;
        let topThreeStadstreck = streck.stadstreck;

        // Rename fields to just streck for easier handling in list component
        topThreeDumstreck = topThreeDumstreck.map(({ _id, person, dumstreck }: DumstreckStats) => ({
            _id,
            person,
            streck: dumstreck,
        }));

        topThreeStadstreck = topThreeStadstreck.map(({ _id, person, stadstreck }: StadstreckStats) => ({
            _id,
            person,
            streck: stadstreck,
        }));

        setTopThreeDumstreck(topThreeDumstreck);
        setTopThreeStadstreck(topThreeStadstreck);
    }, [user.token, logout])


    const getComments = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/comments/getComments`, {
            headers: { Authorization: `Bearer ${user.token}` },
        });

        if (!await checkResponse(response, logout)) return false;

        const comments: Comment[] = await response.json();

        setComments(comments);
    }, [user.token, logout])

    const dumstreckStats = useMemo(() => transformData(dumstreckData), [dumstreckData])
    const stadstreckStats = useMemo(() => transformData(stadstreckData), [stadstreckData]);

    const fetchData = useCallback(async function () {
        await getStats();
        await getTopThreeStats();
        await getComments();
    }, [getStats, getTopThreeStats, getComments])

    const isFetchedRef = useRef(false);

    useEffect(() => {
        if (!isFetchedRef.current) {
            isFetchedRef.current = true;
            if (user) {
                fetchData().catch(console.error);
            }


        }
    }, [fetchData, user]);


    const primaryAxis = React.useMemo(
        (): AxisOptions<Streck> => ({
            getValue: datum => datum.date,
        }),
        []
    )

    const secondaryAxes = React.useMemo(
        (): AxisOptions<Streck>[] => [
            {
                getValue: datum => datum.streck,
                elementType: "line",
            },
        ],
        []
    )

    return (
        <>
            <div className="stats-grid">
                <MyChart
                    setState={setState}
                    activeDatumIndex={activeDatumIndex}
                    activeSeriesIndex={activeSeriesIndex}
                    data={stadstreckStats}
                    primaryAxis={primaryAxis}
                    secondaryAxes={secondaryAxes}
                    dark={dark}
                    title="Städstreck" />
                {
                    topThreeStadstreck.length > 0 ? (
                        <StreckTable data={topThreeStadstreck} title={"Städstreck"} theme={props.theme} />
                    ) :
                        <div className="div-spinner">
                            <Spinner animation="border" role="status" />
                        </div>
                }
            </div>

            <div className="stats-grid">
                <MyChart
                    setState={setState}
                    activeDatumIndex={activeDatumIndex}
                    activeSeriesIndex={activeSeriesIndex}
                    data={dumstreckStats}
                    primaryAxis={primaryAxis}
                    secondaryAxes={secondaryAxes}
                    dark={dark}
                    title="Dumstreck" />

                {
                    topThreeDumstreck.length > 0 ? (
                        <StreckTable data={topThreeDumstreck} title={"Dumstreck"} theme={props.theme} />
                    ) :
                        <div className="div-spinner">
                            <Spinner animation="border" role="status" />
                        </div>
                }
            </div>

            <CommentsTable comments={comments} theme={props.theme} />

        </>


    );
}

function MyChart({
    setState,
    activeDatumIndex,
    activeSeriesIndex,
    data,
    primaryAxis,
    secondaryAxes,
    dark,
    title,
}: any) {

    return (

        <div>
            <ResizableBox
                title={title}
                style={{
                    background: dark ? "#26292c" : "white",
                    boxShadow: dark ? "0 30px 40px rgba(255,255,255,.1)" : "0 30px 40px rgba(0,0,0,.1)",
                    borderStyle: "solid",
                    borderWidth: "0.5px",
                    marginTop: "1rem",
                    padding: ".5rem",
                    borderRadius: "5px",
                }}
            >
                {
                    data.length > 0 ? (
                        <Chart
                            options={{
                                data,
                                primaryAxis,
                                secondaryAxes,
                                dark: dark ? true : false,
                                getDatumStyle: (datum, status) =>
                                    (activeDatumIndex === datum.index &&
                                        activeSeriesIndex === datum.seriesIndex
                                        ? {
                                            opacity: 1,
                                            circle: {
                                                r: 5,
                                            },
                                        }
                                        : activeDatumIndex === datum.index
                                            ? {
                                                opacity: 1,
                                                circle: {
                                                    r: 3,
                                                },
                                            }
                                            : datum.seriesIndex === activeSeriesIndex
                                                ? {
                                                    circle: {
                                                        r: 3,
                                                    },
                                                }
                                                : status === "groupFocused"
                                                    ? {
                                                        circle: {
                                                            r: 2,
                                                        },
                                                    }
                                                    : {
                                                        circle: {
                                                            r: 2,
                                                        },
                                                    }) as any,

                                getSeriesStyle: (series) => {
                                    const colorIndex = series.index % colors.length;

                                    return {
                                        color: colors[colorIndex],
                                        opacity:
                                            activeSeriesIndex > -1
                                                ? series.index === activeSeriesIndex
                                                    ? 1
                                                    : 0.2
                                                : 1,
                                    };
                                },
                                onFocusDatum: (focused) =>
                                    setState({
                                        activeSeriesIndex: focused ? focused.seriesIndex : -1,
                                        activeDatumIndex: focused ? focused.index : -1,
                                    }),
                            }
                            }
                        />
                    ) :
                        <div className="center-chart">
                            <p className="vertical-center">Ingen har fått ett streck än. Bra jobbat!</p>
                        </div>
                }
            </ResizableBox>
        </div>
    );
}
